import React from 'react';
import { Badge, Box, Flex, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ArcButton } from '../../../../../common/components/ArcButton';
import { SEHomeCard } from '../../../../../common/components/SEHomeCard';
import { getFormattedTime } from '../../settings-helpers';
import { DataExport, DataExportStatus } from './types';

type DataExportListItemProps = {
  dataExport: DataExport;
  isDownloading: boolean;
  onDownload: (referenceId: string) => void;
  onRetry: () => void;
};

const STATUS_BADGE_COLOR_SCHEME: Record<DataExportStatus, string> = {
  PENDING: 'gray',
  IN_PROGRESS: 'gray',
  COMPLETED: 'green',
  FAILED: 'red',
};

// Literal `t()` calls (rather than a key lookup map) so the i18n unused-key check can see each key.
function getStatusLabel(t: TFunction, status: DataExportStatus): string {
  switch (status) {
    case 'COMPLETED':
      return t('Data Archive.ready');
    case 'FAILED':
      return t('Data Archive.failed');
    default:
      return t('Data Archive.building');
  }
}

/** Formats an ISO timestamp for display; falls back to the raw value if it can't be parsed. */
function formatTimestamp(isoTimestamp: string | null): string | null {
  if (!isoTimestamp) return null;
  const date = new Date(isoTimestamp);
  if (isNaN(date.getTime())) return isoTimestamp;
  // Date portion picks up the app-wide date-fns locale (see useHighchartSetup); the time portion
  // respects the user's 12/24-hour display preference, same as the rest of the app.
  return `${format(date, 'd MMM yyyy')}, ${getFormattedTime(date)}`;
}

export function DataExportListItem({ dataExport, isDownloading, onDownload, onRetry }: DataExportListItemProps) {
  const { t } = useTranslation();
  const redArcColor = useColorModeValue('#FF3E24', '#FF5B44');
  const spinnerColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  // gray.500 is too low-contrast against the dark SEHomeCard background; customGrey.300 keeps
  // muted text legible there while customGrey.700 matches the light-mode card contrast.
  const mutedTextColor = useColorModeValue('customGrey.700', 'customGrey.300');
  const {
    reference_id: referenceId,
    status,
    requested_at: requestedAt,
    expected_completion: expectedCompletion,
    file_expires_at: fileExpiresAt,
    error,
  } = dataExport;

  const isBuilding = status === 'PENDING' || status === 'IN_PROGRESS';

  return (
    <SEHomeCard mb={3} borderTopLeftRadius="5px" p={4} width="100%" data-testid={`data-export-item-${referenceId}`}>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" color={mutedTextColor}>
          {t('Data Archive.requested on', { date: formatTimestamp(requestedAt) })}
        </Text>
        <Badge colorScheme={STATUS_BADGE_COLOR_SCHEME[status]} data-testid={`data-export-status-${referenceId}`}>
          {getStatusLabel(t, status)}
        </Badge>
      </Flex>

      {isBuilding && (
        <Flex align="center">
          <Spinner size="sm" mr={3} color={spinnerColor} speed="0.8s" />
          <Box>
            <Text>{t('Data Archive.building')}</Text>
            {expectedCompletion && (
              <Text fontSize="sm" color={mutedTextColor}>
                {t('Data Archive.expected by', { date: formatTimestamp(expectedCompletion) })}
              </Text>
            )}
          </Box>
        </Flex>
      )}

      {status === 'COMPLETED' && (
        <Box>
          {fileExpiresAt && (
            <Text fontSize="sm" color={mutedTextColor} mb={3}>
              {t('Data Archive.file expires', { date: formatTimestamp(fileExpiresAt) })}
            </Text>
          )}
          <ArcButton
            arcColor="#3DCD57"
            w="100%"
            isLoading={isDownloading}
            onClick={() => onDownload(referenceId)}
            data-testid={`data-export-download-${referenceId}`}
          >
            {t('Common.download')}
          </ArcButton>
        </Box>
      )}

      {status === 'FAILED' && (
        <Box>
          <Text mb={3}>{error || t('Data Archive.failed description')}</Text>
          <ArcButton arcColor={redArcColor} w="100%" onClick={onRetry} data-testid={`data-export-retry-${referenceId}`}>
            {t('Data Archive.retry')}
          </ArcButton>
        </Box>
      )}
    </SEHomeCard>
  );
}
